import type {
  AdminApiFunnel,
  FunnelAggregates,
  FunnelAggregatesQuery,
  FunnelCreatePayload,
  FunnelExamples,
  FunnelExamplesQuery,
  FunnelListItem,
  FunnelUpdatePayload,
} from "@repo/app-types/api/funnel";
import type { FunnelId } from "@repo/app-types/entities/funnel";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  buildEntityTag,
  buildListTag,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const funnelApi = api.injectEndpoints({
  endpoints: (builder) => ({
    voiceAgentFunnels: builder.query<
      FunnelListItem[],
      TeammateId,
      { data: { funnels: FunnelListItem[] } }
    >({
      query: (teammateId) =>
        `/voice-agents/${encodeURIComponent(teammateId)}/funnels`,
      transformResponse: (response) => response.data.funnels,
      providesTags: (result, _error, teammateId) =>
        getTagsProvidedByList(TagType.Funnel, result, {
          filteredBy: [teammateId],
        }),
    }),
    /** The playbook funnels about a playbook. */
    playbookFunnels: builder.query<
      FunnelListItem[],
      PlaybookId,
      { data: { funnels: FunnelListItem[] } }
    >({
      query: (playbookId) =>
        `/playbooks/${encodeURIComponent(playbookId)}/funnels`,
      transformResponse: (response) => response.data.funnels,
      providesTags: (result, _error, playbookId) =>
        getTagsProvidedByList(TagType.Funnel, result, {
          filteredBy: [playbookId],
        }),
    }),
    funnel: builder.query<
      AdminApiFunnel,
      FunnelId,
      { data: { funnel: AdminApiFunnel } }
    >({
      query: (funnelId) => `/funnels/${encodeURIComponent(funnelId)}`,
      transformResponse: (response) => response.data.funnel,
      providesTags: (_result, _error, funnelId) => [
        buildEntityTag(TagType.Funnel, funnelId),
      ],
    }),
    createFunnel: builder.mutation<
      AdminApiFunnel,
      FunnelCreatePayload,
      { data: { funnel: AdminApiFunnel } }
    >({
      query: (payload) => ({
        url: "/funnels",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => response.data.funnel,
      // The subject's lists: the voice agents', or a playbook funnel's playbook's.
      invalidatesTags: (_result, _error, payload) =>
        (payload.voiceAgentTeammateIds.length > 0
          ? payload.voiceAgentTeammateIds
          : payload.playbookIds
        ).map((subjectId) => buildListTag(TagType.Funnel, [subjectId])),
    }),
    updateFunnel: builder.mutation<
      AdminApiFunnel,
      { funnelId: FunnelId; payload: FunnelUpdatePayload },
      { data: { funnel: AdminApiFunnel } }
    >({
      query: ({ funnelId, payload }) => ({
        url: `/funnels/${encodeURIComponent(funnelId)}`,
        method: "PATCH",
        body: payload,
      }),
      transformResponse: (response) => response.data.funnel,
      // A removed playbook drops its top reps from the numbers at once.
      invalidatesTags: (_result, _error, { funnelId }) => [
        buildEntityTag(TagType.Funnel, funnelId),
        buildEntityTag(TagType.FunnelAggregates, funnelId),
      ],
    }),
    retryFunnelInduction: builder.mutation<
      AdminApiFunnel,
      FunnelId,
      { data: { funnel: AdminApiFunnel } }
    >({
      query: (funnelId) => ({
        url: `/funnels/${encodeURIComponent(funnelId)}/retry-induction`,
        method: "POST",
      }),
      transformResponse: (response) => response.data.funnel,
      invalidatesTags: (_result, _error, funnelId) => [
        buildEntityTag(TagType.Funnel, funnelId),
      ],
    }),
    funnelAggregates: builder.query<
      FunnelAggregates,
      FunnelAggregatesQuery & { funnelId: FunnelId },
      { data: { aggregates: FunnelAggregates } }
    >({
      query: ({ funnelId, voiceAgentTeammateId }) => ({
        url: `/funnels/${encodeURIComponent(funnelId)}/aggregates`,
        params: { voiceAgentTeammateId },
      }),
      transformResponse: (response) => response.data.aggregates,
      providesTags: (_result, _error, { funnelId }) => [
        buildEntityTag(TagType.FunnelAggregates, funnelId),
      ],
    }),
    funnelExamples: builder.query<
      FunnelExamples,
      FunnelExamplesQuery & { funnelId: FunnelId },
      { data: { examples: FunnelExamples } }
    >({
      query: ({ funnelId, momentKey, stageKey, voiceAgentTeammateId }) => ({
        url: `/funnels/${encodeURIComponent(funnelId)}/examples`,
        params: { momentKey, stageKey, voiceAgentTeammateId },
      }),
      transformResponse: (response) => response.data.examples,
      providesTags: (_result, _error, { funnelId }) => [
        buildEntityTag(TagType.FunnelAggregates, funnelId),
      ],
    }),
  }),
});

export const {
  useVoiceAgentFunnelsQuery,
  usePlaybookFunnelsQuery,
  useFunnelQuery,
  useCreateFunnelMutation,
  useUpdateFunnelMutation,
  useRetryFunnelInductionMutation,
  useFunnelAggregatesQuery,
  useFunnelExamplesQuery,
} = funnelApi;
